import { JSX, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserCircleIcon } from '@heroicons/react/24/outline';
import useAppStore from '../stores/useAppStore';

export default function AdminPage(): JSX.Element | null {
  const { isAuthenticated, user } = useAppStore();
  const navigate = useNavigate();

  useEffect(() => {
    // Redirect to home if not authenticated
    if (!isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate, user]);

  // If not authenticated, don't render anything (will redirect)
  if (!isAuthenticated) {
    return null;
  }

  const handleExternalAdmin = (): void => {
    const adminUrl = import.meta.env.VITE_ADMIN_BASE_URL;
    if (adminUrl && adminUrl !== 'https://your-admin-domain.com') {
      window.open(adminUrl, '_blank');
    }
  };

  const getInitials = (name?: string): string => {
    // Use provided initials if available
    if (user?.initials) {
      return user.initials;
    }
    
    // Fallback to generating from name
    if (!name) return 'U';
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Check if we should use an icon instead of initials
  const shouldUseIcon = user?.useIcon || false;

  // Get company name from user
  const getCompanyName = (): string => {
    // First priority: use user.company if available
    if (user?.company) {
      return user.company;
    }
    
    // Second priority: use user's name
    if (user?.name) {
      return user.name;
    }
    
    // Fallback
    return 'Not provided';
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow">
        <div className="container-max section-padding">
          <div className="flex items-center justify-between py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                Admin Dashboard
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Welcome back, {user?.firstName || user?.name || 'User'}
              </p>
            </div>
            <button
              onClick={() => navigate('/')}
              className="btn-ghost"
            >
              ← Back to Website
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container-max section-padding py-12">
        <div className="max-w-4xl mx-auto">
          {/* Welcome Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8 mb-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                {shouldUseIcon ? (
                  <UserCircleIcon className="h-10 w-10" />
                ) : (
                  getInitials(user?.name)
                )}
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                Welcome to Fabrik 28 Admin
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                This is a placeholder admin page. Your actual Cloudscape admin dashboard will be integrated here.
              </p>
              
              {/* User Info */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-500 dark:text-gray-400">Full Name:</span>
                    <p className="text-gray-900 dark:text-gray-100">{user?.name || 'Not provided'}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-500 dark:text-gray-400">Email:</span>
                    <p className="text-gray-900 dark:text-gray-100">{user?.email || 'Not provided'}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-500 dark:text-gray-400">Company:</span>
                    <p className="text-gray-900 dark:text-gray-100">{getCompanyName()}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-500 dark:text-gray-400">Role:</span>
                    <p className="text-gray-900 dark:text-gray-100">{user?.role || 'User'}</p>
                  </div>
                </div>
                
                {/* Additional user details if available */}
                {(user?.firstName || user?.lastName) && (
                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      {user?.firstName && (
                        <div>
                          <span className="font-medium text-gray-500 dark:text-gray-400">First Name:</span>
                          <p className="text-gray-900 dark:text-gray-100">{user.firstName}</p>
                        </div>
                      )}
                      {user?.lastName && (
                        <div>
                          <span className="font-medium text-gray-500 dark:text-gray-400">Last Name:</span>
                          <p className="text-gray-900 dark:text-gray-100">{user.lastName}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => navigate('/admin/docs')}
                  className="btn-primary"
                >
                  📚 View API Documentation
                </button>
                <button
                  onClick={handleExternalAdmin}
                  className="btn-secondary"
                >
                  Open External Admin Dashboard
                </button>
                <button
                  onClick={() => navigate('/')}
                  className="btn-ghost"
                >
                  Return to Marketing Site
                </button>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center">
                  <span className="text-primary-600 dark:text-primary-400 text-xl">🤖</span>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">AI Workflows</h3>
                  <p className="text-gray-600 dark:text-gray-400">Manage your automations</p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-success-100 dark:bg-success-900 rounded-lg flex items-center justify-center">
                  <span className="text-success-600 dark:text-success-400 text-xl">📊</span>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Analytics</h3>
                  <p className="text-gray-600 dark:text-gray-400">View performance metrics</p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 cursor-pointer hover:shadow-md transition-shadow"
                 onClick={() => navigate('/admin/docs')}>
              <div className="flex items-center">
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                  <span className="text-blue-600 dark:text-blue-400 text-xl">📚</span>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">API Docs</h3>
                  <p className="text-gray-600 dark:text-gray-400">View API documentation</p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-warning-100 dark:bg-warning-900 rounded-lg flex items-center justify-center">
                  <span className="text-warning-600 dark:text-warning-400 text-xl">⚙️</span>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Settings</h3>
                  <p className="text-gray-600 dark:text-gray-400">Configure your account</p>
                </div>
              </div>
            </div>
          </div>

          {/* Integration Notice */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <span className="text-blue-600 dark:text-blue-400 text-xl">ℹ️</span>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">
                  Admin Dashboard & API Documentation Ready
                </h3>
                <div className="mt-2 text-sm text-blue-700 dark:text-blue-300">
                  <p>
                    This admin area is ready for your Cloudscape dashboard integration and includes API documentation access.
                  </p>
                  <ul className="mt-2 list-disc list-inside space-y-1">
                    <li>User authentication is preserved across all admin routes</li>
                    <li>Route protection is implemented for /admin and /admin/docs</li>
                    <li>API documentation is accessible at /admin/docs for authenticated users</li>
                    <li>User data is available via useAppStore throughout the admin area</li>
                    <li>External admin dashboard can be opened in new tab</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
